import type { IzidBralnegaTesta, MinimaxOrganizacija, PovezavaPodjetjaZMinimaxom, PreizkusSifranta } from '@koda/shared';
import { useCallback, useEffect, useState, type JSX } from 'react';

import { api } from '../lib/api';
import { datumInUra, decimalno } from '../lib/format';

const STATUSI: { koda: 'O' | 'P' | 'A'; naziv: string }[] = [
  { koda: 'P', naziv: 'potrjene (P)' },
  { koda: 'A', naziv: 'samodejne (A)' },
  { koda: 'O', naziv: 'osnutki (O)' },
];

/** Vrednost polja iz vzorca, berljivo in kratko. */
function prikaz(v: unknown): string {
  if (v === null || v === undefined) return '—';
  if (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean') return String(v);
  return JSON.stringify(v);
}

/** Pokritost enega sifranta iz bralnega testa: stevila, nerazreseni ID in surova vnosa. */
function Sifrant({ naziv, p }: { naziv: string; p: PreizkusSifranta }): JSX.Element {
  if (p.sklicevanih === 0) {
    return (
      <li>
        <span>{naziv}</span>
        <span>knjižbe se nanj ne sklicujejo; seznam ni bil prebran</span>
      </li>
    );
  }
  if (p.napaka !== null) {
    return (
      <li>
        <span>{naziv}</span>
        <span className="napaka">seznama ni bilo mogoče prebrati: {p.napaka}</span>
      </li>
    );
  }
  return (
    <li className="sifrant">
      <span>{naziv}</span>
      <span>
        napovedanih {p.napovedanih === null ? '—' : decimalno(String(p.napovedanih), 0)}, prebranih{' '}
        {decimalno(String(p.prebranih), 0)}
        {p.brezSifre > 0 && `, brez šifre ${decimalno(String(p.brezSifre), 0)}`}; v knjižbah leta{' '}
        {decimalno(String(p.sklicevanih), 0)} različnih, od tega{' '}
        {p.niNaSeznamu === 0 ? (
          <strong>vsi na seznamu</strong>
        ) : (
          <strong className="napaka">{decimalno(String(p.niNaSeznamu), 0)} jih na seznamu ni (uvoz se ustavi)</strong>
        )}
        {p.naSeznamuBrezSifre > 0 &&
          `, ${decimalno(String(p.naSeznamuBrezSifre), 0)} na seznamu brez šifre (Minimax je ne zahteva; uvoz jih prevzame brez šifre, z nazivom, kot izvoz)`}
        {p.nerazresenih > 0 && (
          <>
            {' '}
            (ID {p.nerazreseniId.join(', ')}
            {p.nerazresenih > p.nerazreseniId.length && ` in še ${decimalno(String(p.nerazresenih - p.nerazreseniId.length), 0)}`})
          </>
        )}
        {' · '}
        {p.klicev} klicev
        {p.polja.length > 0 && (
          <details>
            <summary>Prvi vnos seznama ({p.polja.join(', ')})</summary>
            <pre>{JSON.stringify(p.prviVnos, null, 2)}</pre>
          </details>
        )}
        {p.nerazresenih > 0 && (
          <details>
            <summary>Vnos ID {p.nerazreseniId[0]}, prebran posamezno</summary>
            {p.posamezniVnosNapaka !== null ? (
              <p className="napaka">{p.posamezniVnosNapaka}</p>
            ) : (
              <pre>{JSON.stringify(p.posamezniVnos, null, 2)}</pre>
            )}
          </details>
        )}
      </span>
    </li>
  );
}

/**
 * Povezava podjetja z Minimaxom prek API in bralni test (docs/ARHITEKTURA.md,
 * 5.10). Samo skrbnik; kartica je na strani uvoza podjetij z virom Minimax.
 * Nicesar ne uvozi: bralni test pokaze, kaj API vrne, da se uvoz lahko
 * zgradi na dejanskih poljih.
 */
/** Danasnji datum `llll-mm-dd` po lokalnem casu. */
function danes(): string {
  const d = new Date();
  return `${String(d.getFullYear())}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** Privzeti datum do: danes, a najvec zadnji dan leta. */
function privzetiDo(leto: number): string {
  const d = danes();
  const konec = `${String(leto)}-12-31`;
  return d < `${String(leto)}-01-01` ? `${String(leto)}-01-01` : d > konec ? konec : d;
}

/** Datum do je veljaven, ce je znotraj leta in ne po privzetem (danes oziroma konec leta). */
function veljavenDo(leto: number, d: string): boolean {
  return /^\d{4}-\d{2}-\d{2}$/.test(d) && d >= `${String(leto)}-01-01` && d <= privzetiDo(leto);
}

interface Lastnosti {
  podjetje: string;
  leto: number;
  /**
   * Uvoz prek API je sprejet in teče v ozadju; stran uvoza prevzame
   * spremljanje stanja, enako kot pri datoteki.
   */
  naUvoz?: (uvoz: string) => void;
}

export function MinimaxPovezava({ podjetje, leto, naUvoz }: Lastnosti): JSX.Element {
  const [stanje, setStanje] = useState<PovezavaPodjetjaZMinimaxom | null>(null);
  // Datum do je vezan na leto, za katero je bil izbran: ob menjavi leta na
  // strani uvoza (ista komponenta, nov `leto`) se vrne na privzeti datum
  // novega leta, da gumb ne poslje datuma iz prejsnjega leta.
  const [izborDo, setIzborDo] = useState(() => ({ leto, do: privzetiDo(leto) }));
  const doDatuma = izborDo.leto === leto ? izborDo.do : privzetiDo(leto);
  const setDoDatuma = (d: string): void => {
    setIzborDo({ leto, do: d });
  };
  const doVeljaven = veljavenDo(leto, doDatuma);
  const [uvozTece, setUvozTece] = useState(false);
  const [napaka, setNapaka] = useState<string | null>(null);
  const [organizacije, setOrganizacije] = useState<MinimaxOrganizacija[] | null>(null);
  const [izbrana, setIzbrana] = useState('');
  const [klicevSeznama, setKlicevSeznama] = useState(0);
  const [delam, setDelam] = useState<'seznam' | 'shrani' | 'test' | null>(null);
  const [izid, setIzid] = useState<IzidBralnegaTesta | null>(null);

  const osvezi = useCallback((): void => {
    api
      .minimaxPovezava(podjetje)
      .then(setStanje)
      .catch((e: unknown) => {
        setNapaka(e instanceof Error ? e.message : 'Povezave ni bilo mogoče prebrati.');
      });
  }, [podjetje]);
  useEffect(osvezi, [osvezi]);

  const sporoci = (e: unknown, sicer: string): void => {
    setNapaka(e instanceof Error ? e.message : sicer);
  };

  const prikaziOrganizacije = (): void => {
    setNapaka(null);
    setDelam('seznam');
    api
      .minimaxOrganizacije(podjetje)
      .then((o) => {
        setOrganizacije(o.organizacije);
        setKlicevSeznama(o.klicevApi);
        setIzbrana(o.predlog ?? stanje?.povezava?.organizacijaId ?? o.organizacije[0]?.id ?? '');
      })
      .catch((e: unknown) => {
        sporoci(e, 'Organizacij ni bilo mogoče prebrati.');
      })
      .finally(() => {
        setDelam(null);
      });
  };

  const povezi = (): void => {
    const o = organizacije?.find((x) => x.id === izbrana);
    if (!o) return;
    setNapaka(null);
    setDelam('shrani');
    api
      .shraniMinimaxPovezavo(podjetje, { organizacijaId: o.id, naziv: o.naziv })
      .then(() => {
        setOrganizacije(null);
        setIzid(null);
        osvezi();
      })
      .catch((e: unknown) => {
        sporoci(e, 'Povezave ni bilo mogoče shraniti.');
      })
      .finally(() => {
        setDelam(null);
      });
  };

  const odstrani = (): void => {
    if (!window.confirm('Odstranim povezavo podjetja z Minimaxom? Knjižbe ostanejo.')) return;
    setNapaka(null);
    api
      .odstraniMinimaxPovezavo(podjetje)
      .then(() => {
        setIzid(null);
        osvezi();
      })
      .catch((e: unknown) => {
        sporoci(e, 'Povezave ni bilo mogoče odstraniti.');
      });
  };

  const testiraj = (): void => {
    setNapaka(null);
    setIzid(null);
    setDelam('test');
    api
      .minimaxBralniTest(podjetje, leto)
      .then((i) => {
        setIzid(i);
        osvezi();
      })
      .catch((e: unknown) => {
        sporoci(e, 'Bralni test ni uspel.');
      })
      .finally(() => {
        setDelam(null);
      });
  };

  const povezava = stanje?.povezava ?? null;

  const uvozi = (): void => {
    if (!doVeljaven) return;
    const potrjeno = window.confirm(
      `Knjižbe leta ${String(leto)} bodo prebrane iz Minimaxa prek API za obdobje 1. 1. ${String(leto)} – ${doDatuma.slice(8, 10)}. ${doDatuma.slice(5, 7)}. ${doDatuma.slice(0, 4)} ` +
        '(potrjene in samodejne temeljnice; osnutki ne). Obstoječi uvoz tega leta bo nadomeščen. Nadaljujem?',
    );
    if (!potrjeno) return;
    setNapaka(null);
    setUvozTece(true);
    api
      .minimaxUvoz(podjetje, { leto, do: doDatuma })
      .then((odgovor) => {
        naUvoz?.(odgovor.uvoz);
      })
      .catch((e: unknown) => {
        sporoci(e, 'Uvoza prek API ni bilo mogoče začeti.');
      })
      .finally(() => {
        setUvozTece(false);
      });
  };

  return (
    <section className="kartica minimax-povezava">
      <h2>Minimax (API)</h2>
      <p className="podnaslov">
        Portal bere knjižbe neposredno iz Minimaxa, z enim dostopom servisa in izrecno povezavo
        podjetja na njegovo organizacijo. Samo branje; bralni test ničesar ne uvozi.
      </p>

      {stanje === null && napaka === null && <p className="drobno">Berem …</p>}

      {stanje !== null && !stanje.stanje.nastavljen && (
        <p className="opozorilo">
          Minimax ni nastavljen: na Renderju manjka {stanje.stanje.manjka.join(', ')}. Navodila so v
          `docs/MINIMAX-API.md`.
        </p>
      )}

      {stanje?.stanje.nastavljen === true && (
        <>
          {povezava === null ? (
            <p>Podjetje še ni povezano z Minimax organizacijo.</p>
          ) : (
            <p>
              Povezano z <strong>{povezava.naziv}</strong>{' '}
              <span className="drobno">(ID {povezava.organizacijaId})</span>
              <br />
              <span className="drobno">
                shranjeno {datumInUra(povezava.shranjeno)}
                {povezava.zadnjiTest !== null && ` · zadnji bralni test ${datumInUra(povezava.zadnjiTest)}`}
              </span>
            </p>
          )}

          <div className="gumbi">
            <button type="button" onClick={prikaziOrganizacije} disabled={delam !== null}>
              {delam === 'seznam' ? 'Berem organizacije …' : povezava === null ? 'Prikaži organizacije' : 'Zamenjaj organizacijo'}
            </button>
            {povezava !== null && (
              <>
                <button type="button" onClick={testiraj} disabled={delam !== null}>
                  {delam === 'test' ? 'Testiram … (nekaj deset klicev)' : `Bralni test za leto ${String(leto)}`}
                </button>
                <button type="button" className="tiho" onClick={odstrani} disabled={delam !== null}>
                  Odstrani povezavo
                </button>
              </>
            )}
          </div>

          {povezava !== null && naUvoz !== undefined && (
            <div className="obrazec uvoz-api">
              <label>
                Beri knjižbe do datuma
                <input
                  type="date"
                  value={doDatuma}
                  min={`${String(leto)}-01-01`}
                  max={privzetiDo(leto)}
                  onChange={(d) => {
                    setDoDatuma(d.target.value);
                  }}
                />
              </label>
              <button type="button" onClick={uvozi} disabled={delam !== null || uvozTece || !doVeljaven}>
                {uvozTece ? 'Začenjam uvoz …' : `Uvozi knjižbe leta ${String(leto)} prek API`}
              </button>
              <span className="drobno">
                Potrjene in samodejne temeljnice od 1. 1. do izbranega datuma; osnutki ne. Pokritost bo
                zadnji cel mesec pred datumom. Nadomesti obstoječi uvoz tega leta.
              </span>
              {!doVeljaven && (
                <span className="napaka">
                  Datum do mora biti v letu {String(leto)} in ne v prihodnosti.
                </span>
              )}
            </div>
          )}

          {organizacije !== null && (
            <div className="obrazec">
              {organizacije.length === 0 ? (
                <p className="opozorilo">
                  Uporabnik Minimaxa nima dostopa do nobene organizacije. V Minimaxu mu dodelite
                  organizacijo stranke.
                </p>
              ) : (
                <>
                  <label>
                    Minimax organizacija
                    <select
                      value={izbrana}
                      onChange={(d) => {
                        setIzbrana(d.target.value);
                      }}
                    >
                      {organizacije.map((o) => (
                        <option key={o.id} value={o.id}>
                          {o.naziv} ({o.id})
                        </option>
                      ))}
                    </select>
                  </label>
                  <button type="button" onClick={povezi} disabled={delam !== null || izbrana === ''}>
                    {delam === 'shrani' ? 'Shranjujem …' : 'Poveži'}
                  </button>
                  <span className="drobno">
                    {organizacije.length} organizacij · {klicevSeznama} klicev API
                  </span>
                </>
              )}
            </div>
          )}
        </>
      )}

      {napaka !== null && <p className="napaka">{napaka}</p>}

      {izid !== null && (
        <div className="bralni-test">
          <h3>Bralni test · {izid.organizacija.naziv}</h3>
          <ul className="seznam">
            <li>
              <span>Dostop do organizacije</span>
              <span>{izid.dostopDoOrganizacije ? 'da' : 'NE – organizacije ni na seznamu uporabnika'}</span>
            </li>
            <li>
              <span>Organizacij na seznamu</span>
              <span>{izid.steviloOrganizacij}</span>
            </li>
            <li>
              <span>Obdobje</span>
              <span>
                {izid.obdobje.od} – {izid.obdobje.do}
              </span>
            </li>
            <li>
              <span>Knjižb skupaj</span>
              <span>{decimalno(String(izid.skupajKnjizb), 0)}</span>
            </li>
            {STATUSI.map((s) => (
              <li key={s.koda}>
                <span>Knjižb {s.naziv}</span>
                <span>{decimalno(String(izid.poStatusu[s.koda]), 0)}</span>
              </li>
            ))}
            <li>
              <span>Največja stran (zahtevanih {izid.stranicenje.zahtevanih})</span>
              <span>{izid.stranicenje.vrnjenih} vrstic</span>
            </li>
            <li>
              <span>Stranjenje po JournalEntryId</span>
              <span>
                {izid.stranicenje.idNarascajo ? 'ID naraščajo' : 'ID NE naraščajo'},{' '}
                {izid.stranicenje.brezPrekrivanja ? 'strani se ne prekrivata' : 'strani se PREKRIVATA'}
              </span>
            </li>
            <li>
              <span>Klicev API</span>
              <span>{izid.klicevApi}</span>
            </li>
          </ul>

          <h4>Šifranti za uvoz prek API (sklici knjižb leta s statusom P ali A)</h4>
          <ul className="seznam sifranti">
            <Sifrant naziv="Stranke" p={izid.sifranti.stranke} />
            <Sifrant naziv="Analitike" p={izid.sifranti.analitike} />
          </ul>

          <h4>Pomembna polja (prva neprazna vrednost)</h4>
          <table className="polja">
            <tbody>
              {Object.entries(izid.primeri).map(([polje, v]) => (
                <tr key={polje}>
                  <th>{polje}</th>
                  <td>
                    <code>{prikaz(v)}</code>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="drobno">Vsa polja v vzorcu: {izid.polja.join(', ')}</p>

          <details>
            <summary>Surov odgovor API (prvih {izid.vzorec.length} knjižb)</summary>
            <pre>{JSON.stringify(izid.vzorec, null, 2)}</pre>
          </details>
        </div>
      )}
    </section>
  );
}
